/**
 * Cookie ngôn ngữ GoPaperless Agent (agent.quantera.vn)
 *
 * - cookie_accesslang_agentquantera  : ngôn ngữ của Agent — CHỈ dùng trên site này
 * - cookie_preferences_quantera_agent: preferences (functional / analytics / marketing)
 *
 * KHÔNG đụng cookie_accesslang_quantera — cookie đó thuộc quantera.vn (site khác).
 * Chỉ ghi/cập nhật cookie ngôn ngữ Agent khi preferences.functional === true.
 */

export const LANG_COOKIE_NAME = "cookie_accesslang_agentquantera";
export const PREFS_COOKIE_NAME = "cookie_preferences_quantera_agent";
export const CONSENT_SYNC_KEY = "cookie_consent_sync_agentquantera";

const LANG_STORAGE_KEY = "lang";
const MAX_AGE = 31536000; // 1 năm

function hostname() {
  try {
    return window.location.hostname || "";
  } catch {
    return "";
  }
}

/** Domain scope chỉ cho Agent — không ghi .quantera.vn */
function agentDomainAttr() {
  if (typeof window === "undefined") return "";
  const host = hostname();
  if (host === "agent.quantera.vn" || host.endsWith(".agent.quantera.vn")) {
    return "; domain=.agent.quantera.vn";
  }
  return "";
}

function safeSetCookie(cookieString) {
  try {
    document.cookie = cookieString;
  } catch {
    // Trình duyệt chặn cookie — không crash UI
  }
}

/**
 * Xóa cookie Agent theo tên (host-only + .agent.quantera.vn).
 * Không xóa cookie trên .quantera.vn (site Quantera chính).
 */
function expireAgentCookieByName(name) {
  if (typeof document === "undefined") return;
  const base = `${name}=; path=/; max-age=0; SameSite=Lax`;
  safeSetCookie(base);
  safeSetCookie(
    `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`
  );
  safeSetCookie(`${base}; domain=.agent.quantera.vn`);
  safeSetCookie(
    `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; domain=.agent.quantera.vn`
  );
}

function readCookieRaw(name) {
  if (typeof document === "undefined") return null;
  try {
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`));
    if (!match) return null;
    return decodeURIComponent(match.split("=").slice(1).join("="));
  } catch {
    return null;
  }
}

function normalizeLang(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "en" || normalized === "vi" ? normalized : null;
}

/** Đọc cookie_preferences_quantera_agent — luôn đọc lại từ document.cookie (cross-tab) */
export function getCookiePreferences() {
  const raw = readCookieRaw(PREFS_COOKIE_NAME);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Chỉ cho phép cookie ngôn ngữ khi functional === true (đọc live, không cache) */
export function isFunctionalCookieAllowed() {
  try {
    return getCookiePreferences()?.functional === true;
  } catch {
    return false;
  }
}

/** Đọc ngôn ngữ Agent hợp lệ: "en" | "vi" | null (bỏ qua "ja" và giá trị lạ) */
export function getAccessLangCookie() {
  return normalizeLang(readCookieRaw(LANG_COOKIE_NAME));
}

function persistLangStorage(lang) {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // ignore
  }
}

function clearLangStorage() {
  try {
    localStorage.removeItem(LANG_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Lưu ngôn ngữ vào cookie_accesslang_agentquantera.
 * Không ghi cookie_accesslang_quantera (thuộc quantera.vn).
 * Trả false nếu functional tắt / giá trị không hợp lệ / cookie bị chặn — không throw.
 */
export function setAccessLangCookie(lang, { force = false } = {}) {
  if (typeof document === "undefined") return false;
  try {
    if (!force && !isFunctionalCookieAllowed()) return false;

    const normalized = normalizeLang(lang);
    if (!normalized) return false;

    const common = `; path=/; max-age=${MAX_AGE}; SameSite=Lax`;

    // Xóa bản Agent cũ (kể cả value lạ) rồi ghi lại
    expireAgentCookieByName(LANG_COOKIE_NAME);

    safeSetCookie(
      `${LANG_COOKIE_NAME}=${encodeURIComponent(normalized)}${common}${agentDomainAttr()}`
    );

    persistLangStorage(normalized);
    return true;
  } catch {
    return false;
  }
}

/**
 * Xóa cookie ngôn ngữ Agent.
 * Không đụng cookie_accesslang_quantera của quantera.vn.
 */
export function clearAccessLangCookie() {
  try {
    expireAgentCookieByName(LANG_COOKIE_NAME);
    clearLangStorage();
  } catch {
    // Cookie đã hết hạn / bị chặn — không crash
  }
}

export function saveCookiePreferences(prefs) {
  if (typeof document === "undefined") return;
  try {
    const normalized = {
      functional: prefs?.functional === true,
      analytics: prefs?.analytics === true,
      marketing: prefs?.marketing === true,
    };
    expireAgentCookieByName(PREFS_COOKIE_NAME);
    safeSetCookie(
      `${PREFS_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(normalized))}` +
        `; path=/; max-age=${MAX_AGE}; SameSite=Lax` +
        agentDomainAttr()
    );
  } catch {
    // ignore
  }
}

/**
 * Áp dụng preferences SAU KHI user bấm Lưu / Accept / Reject.
 * functional true  → tạo/cập nhật cookie_accesslang_agentquantera theo currentLang
 * functional false → xóa cookie_accesslang_agentquantera (không xóa cookie Quantera)
 */
export function applyCookiePreferences(prefs, currentLang) {
  const next = {
    functional: prefs?.functional === true,
    analytics: prefs?.analytics === true,
    marketing: prefs?.marketing === true,
  };

  saveCookiePreferences(next);

  if (next.functional) {
    setAccessLangCookie(currentLang, { force: true });
  } else {
    clearAccessLangCookie();
  }

  notifyCookieConsentChanged();
}

function notifyCookieConsentChanged() {
  try {
    localStorage.setItem(CONSENT_SYNC_KEY, String(Date.now()));
  } catch {
    // ignore
  }
  try {
    window.dispatchEvent(new Event("cookie-consent-changed"));
  } catch {
    // ignore
  }
}

export function subscribeCookieConsentChange(handler) {
  if (typeof window === "undefined") return () => {};

  const onLocal = () => {
    try {
      handler();
    } catch {
      // ignore
    }
  };
  const onStorage = (e) => {
    if (e.key === CONSENT_SYNC_KEY) onLocal();
  };
  const onFocus = () => onLocal();

  window.addEventListener("cookie-consent-changed", onLocal);
  window.addEventListener("storage", onStorage);
  window.addEventListener("focus", onFocus);

  return () => {
    window.removeEventListener("cookie-consent-changed", onLocal);
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("focus", onFocus);
  };
}

/**
 * Redirect `/`: chỉ dùng cookie_accesslang_agentquantera khi functional === true.
 */
export function resolveAccessLang(fallback = "en") {
  if (!isFunctionalCookieAllowed()) return fallback;
  return getAccessLangCookie() || fallback;
}

/** Ngôn ngữ hiện tại từ URL path */
export function getPathLang(pathname) {
  try {
    const path = pathname || window.location.pathname || "";
    return path.split("/")[1] === "vi" ? "vi" : "en";
  } catch {
    return "en";
  }
}
